import { z } from "zod";
import type {
  PhotoCaption,
  PhotoCredit,
  PostOverride,
  PostOverridePhoto,
} from "./postOverrides";

/**
 * Runtime validation for src/content/postOverrides.json.
 *
 * The same schema is imported from two places:
 *   - The SPA bundle uses it to fail-fast at module load when the JSON
 *     is broken, and pre-flights admin edits before sending them to the
 *     server.
 *   - Cloudflare Pages Functions (/api/admin/posts/[slug].ts) use it as
 *     the authoritative gate on any write, so a compromised or buggy
 *     browser can never commit a malformed entry.
 *
 * Keep this file a pure mirror of the TS interfaces in postOverrides.ts.
 * If those interfaces ever drift from the schema, the z.ZodType<T>
 * assertions below will fail to compile — which is exactly the wanted
 * signal.
 */

export const PhotoCaptionSchema: z.ZodType<PhotoCaption> = z
  .object({
    ar: z.string().optional(),
    en: z.string().optional(),
  })
  .strict();

export const PhotoCreditSchema: z.ZodType<PhotoCredit> = z
  .object({
    author: z.string().min(1),
    license: z.string().min(1),
    licenseUrl: z.string().url().optional(),
    sourceUrl: z.string().url().optional(),
    source: z.string().optional(),
  })
  .strict();

export const PostOverridePhotoSchema: z.ZodType<PostOverridePhoto> = z
  .object({
    src: z.string().min(1),
    alt: z.string(),
    credit: PhotoCreditSchema,
  })
  .strict();

export const PostOverrideSchema: z.ZodType<PostOverride> = z
  .object({
    titleAr: z.string().optional(),
    titleEn: z.string().optional(),
    textAr: z.string().optional(),
    textEn: z.string().optional(),
    photos: z.array(PostOverridePhotoSchema).optional(),
    photoCaptions: z.array(PhotoCaptionSchema).optional(),
    removedPhotos: z.array(z.number().int().nonnegative()).optional(),
    exclude: z.boolean().optional(),
    classification: z.string().optional(),
  })
  .strict();

export const PostOverridesFileSchema = z.record(
  z.string(),
  PostOverrideSchema,
);

/**
 * Parse a raw value (typically the default export of
 * postOverrides.json) and return a typed Record. Throws a descriptive
 * ZodError when the shape is wrong — the error message names the exact
 * slug + field path that failed, so "admin wrote a bad JSON" is never
 * an opaque build break.
 */
export function parsePostOverrides(
  raw: unknown,
): Record<string, PostOverride> {
  return PostOverridesFileSchema.parse(raw);
}
